// EditorTitleBar.jsx — Shared 48 px titlebar for full-screen editor modals.
//
// Renders: title (editable when onTitleChange provided) | centerSlot | close | rightSlot
//
// Props:
//   title            — display string (null renders nothing)
//   onTitleChange    — if provided, title is rendered as an editable InputBase
//   onClose          — action handler
//   centerSlot       — optional React node rendered between title and actions
//                      (geocoder toggle for map, zoom controls for annotation)
//   rightSlot        — optional React node after close button (presence chip etc.)
//
// Ticket 03: undo/redo/delete-selected used to render here too, duplicating the equivalent
// controls each consumer already renders in its own canvas-local toolbar (MapDrawToolbar's
// canvas variant for the map editor, AnnotationDrawToolbar for the annotation editor — see
// that file for the backfilled undo/redo/delete buttons). Removed entirely from this shared
// component; each modal now owns its own single copy of those controls.

import React, {memo} from 'react';
import {Stack, Box, InputBase, IconButton, Tooltip, Typography} from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';

const EditorTitleBar = memo(function EditorTitleBar({
    title,
    onTitleChange,
    onClose,
    centerSlot,
    rightSlot,
}) {
    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={(theme) => ({
                px: 1.5, height: 48, flexShrink: 0,
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
            })}
        >
            {title != null && (
                onTitleChange ? (
                    // Ticket 03: editable title had no visible affordance that it's editable —
                    // no border/underline even on hover/focus, so it read as a static label.
                    // Add a subtle bottom border that only appears on hover/focus (transparent
                    // otherwise) so the field doesn't shift layout when the border appears.
                    <InputBase
                        value={title}
                        onChange={onTitleChange}
                        placeholder="Untitled"
                        sx={(theme) => ({
                            fontSize: 14, fontWeight: 600, minWidth: 120, maxWidth: 240,
                            borderBottom: '1px solid transparent',
                            transition: 'border-color 0.1s',
                            '&:hover': {borderBottom: `1px solid ${theme.palette.divider}`},
                            '&.Mui-focused': {borderBottom: `1px solid ${theme.palette.primary.main}`},
                        })}
                    />
                ) : (
                    <Typography sx={{fontSize: 14, fontWeight: 600, minWidth: 60, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        {title}
                    </Typography>
                )
            )}

            {centerSlot && <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>{centerSlot}</Box>}

            <Box sx={{flex: 1}}/>

            <Tooltip title="Close">
                <IconButton size="small" onClick={onClose} aria-label="Close">
                    <CloseRounded sx={{fontSize: 18}}/>
                </IconButton>
            </Tooltip>

            {rightSlot && <Box sx={{display: 'flex', alignItems: 'center'}}>{rightSlot}</Box>}
        </Stack>
    );
});

export default EditorTitleBar;
